import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CritStatItem, QualityConsultantReportResponse, type HiddenCriterionRef } from '@/entities/dashboards';
import { cn, QualityZoneColor, type QualityZoneRanges } from '@/shared/lib';
import { isCriterionHidden } from '../model/hiddenCriteria';
import { critZoneByMax, fmt } from './utils';
import { StatBox } from './parts/StatBox';
import { DeltaValue } from './parts/DeltaValue';
import { ZoneCriteriaColumn } from './ZoneCriteriaColumn';
import { CritStatsTable } from './CritStatsTable';

/** Фиксированный порядок блоков критериев: зелёная → жёлтая (нейтральная) → красная зона. */
const ZONE_ORDER: QualityZoneColor[] = ['green', 'yellow', 'red'];

type Props = {
  report: QualityConsultantReportResponse;
  /** Критерии, скрытые пользователем (каждый — в разрезе своего заключения). */
  hiddenCriteria: HiddenCriterionRef[];
  /** Пользовательские пороги зон, по которым критерии раскладываются по зонным блокам. */
  zoneRanges: QualityZoneRanges;
};

/** Содержимое мини-отчёта по консультанту: KPI, критерии по зонам, динамика и таблица отклонений. */
export const ConsultantReportBody: FC<Props> = ({ report, hiddenCriteria, zoneRanges }) => {
  const { t } = useTranslation('qualityDashboard');

  // Скрытые пользователем критерии исключаются из отображения мини-отчёта — и из зонных
  // блоков, и из таблицы отклонений (на агрегаты и сами оценки это не влияет). Критерий
  // адресуется вместе со своим заключением, поэтому снятая галочка убирает его только в
  // своём заключении: одноимённый критерий соседнего остаётся со своими баллами и шкалой.
  const visibleCrits = useMemo(
    () => report.critStats.filter((item) => !isCriterionHidden(hiddenCriteria, item.expertReportId, item.name)),
    [report.critStats, hiddenCriteria],
  );

  // Критерии раскладываются по зонам качества: каждый попадает ровно в один блок —
  // по доле своего балла от максимума шкалы относительно пользовательских порогов зон.
  // Критерий без определимой зоны (нет балла или максимума) в зонные блоки не входит,
  // поэтому картина однозначна: расположение критерия само и есть его зона качества.
  const critsByZone = useMemo(() => {
    const groups: Record<QualityZoneColor, CritStatItem[]> = { green: [], yellow: [], red: [] };
    for (const item of visibleCrits) {
      const zone = critZoneByMax(item.consultantAvg, item.maxScore, zoneRanges);
      if (zone) groups[zone].push(item);
    }
    return groups;
  }, [visibleCrits, zoneRanges]);

  return (
    <div className="flex flex-col gap-6">
      {/* Шапка: имя консультанта и количество проверок */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
        <div className="border border-blue-200 bg-gradient-to-br from-blue-50 to-white rounded-xl p-4">
          <div className="text-xl font-bold">{report.consultant}</div>
        </div>
        <StatBox
          label={t('consultantReport.reportsCount')}
          value={report.count}
          className="rounded-xl"
          valueClassName="text-3xl"
        />
      </div>

      {/* KPI блок */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <StatBox label={t('consultantReport.avgScore')} value={fmt(report.avgTotal)} className="rounded-xl" />
        <StatBox
          label={t('consultantReport.teamAvg')}
          value={fmt(report.teamAvgTotal)}
          className="rounded-xl"
        />
        <StatBox
          label={t('consultantReport.teamDeviation')}
          value={<DeltaValue value={report.totalDiff} />}
          className="rounded-xl"
          valueClassName={cn('text-2xl', !report.totalDiff && 'text-muted-foreground')}
        />
        <StatBox
          label={t('consultantReport.lowScorePct')}
          value={`${fmt(report.lowShare, 0)}%`}
          className="rounded-xl"
        />
      </div>

      {/* Критерии по зонам качества: зелёная / жёлтая / красная зона, слева направо */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {ZONE_ORDER.map((zone) => (
          <ZoneCriteriaColumn key={zone} zone={zone} items={critsByZone[zone]} />
        ))}
      </div>

      {/* Динамика последние 5 vs предыдущие 5 */}
      <div className="border rounded-xl p-4">
        <h4 className="font-medium mb-3">{t('consultantReport.dynamics')}</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatBox label={t('consultantReport.last5')} value={fmt(report.last5Avg)} />
          <StatBox label={t('consultantReport.prev5')} value={fmt(report.prev5Avg)} />
          <StatBox
            label={t('consultantReport.change')}
            value={<DeltaValue value={report.recentDelta} />}
            valueClassName={cn('text-2xl', !report.recentDelta && 'text-muted-foreground')}
          />
        </div>
      </div>

      {/* Таблица отклонений по критериям */}
      <div className="border rounded-xl p-4">
        <h4 className="font-medium mb-3">{t('consultantReport.criteriaTable')}</h4>
        <CritStatsTable items={visibleCrits} zoneRanges={zoneRanges} />
      </div>
    </div>
  );
};
